import {
  OnPageSeoProject,
  OnPageChecklistReviewItem,
  MultiServiceLocationPlan,
  NapProfile,
  PageBlueprintItem,
  TrustSignalItem
} from '../types';
import { cloudSyncService } from './cloudSyncService';

const ONPAGE_PROJECTS_KEY = 'zynex_ninja_onpage_projects_v1';
const ACTIVE_ONPAGE_PROJECT_KEY = 'zynex_ninja_active_onpage_project_id';

export const DEFAULT_PAGE_BLUEPRINTS: PageBlueprintItem[] = [
  // 1. Core Primary Pages
  {
    id: 'bp-home',
    pageType: 'homepage',
    category: 'core',
    title: 'Homepage',
    targetUrl: '/',
    isPlanned: true,
    isBuilt: true,
    purpose: 'Main landing page presenting core value proposition, primary city focus, service overview, and prominent global NAP.',
    keyElements: ['Hero H1 with primary target keyword & city', 'Value proposition & trust badges', 'Services summary grid with links', 'Google Maps embed & global NAP footer', 'Primary CTA (Call Now / Request Quote)'],
  },
  {
    id: 'bp-about',
    pageType: 'about',
    category: 'core',
    title: 'About Us / Company Profile',
    targetUrl: '/about-us',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Build E-E-A-T trust signals by showcasing company experience, leadership bios, certifications, mission, and why customers choose you.',
    keyElements: ['Founder / Team real photos & bios', 'Years in business & local community involvement', 'Licenses & certifications', 'Core company values & mission'],
  },
  {
    id: 'bp-contact',
    pageType: 'contact',
    category: 'core',
    title: 'Contact Us',
    targetUrl: '/contact-us',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Direct conversion hub providing 100% exact matching NAP, interactive Google Map, driving directions, inquiry form, and working phone number.',
    keyElements: ['Exact Name, Address, Phone (NAP)', 'Embedded Google Maps iframe', 'Contact inquiry form with validation', 'Business hours & parking/directions info', 'Click-to-call mobile button'],
  },

  // 2. Service Pages Architecture (Dedicated subpages for each individual service)
  {
    id: 'bp-srv-1',
    pageType: 'service',
    category: 'service',
    title: 'Web Design Service Page',
    targetUrl: '/services/web-design',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated page targeting primary web design keywords with comprehensive service explanation, process, and portfolio highlights.',
    keyElements: ['Unique H1 with primary service keyword', 'Detailed scope of service & deliverables', 'Case study / work showcase snippet', 'Client testimonials & FAQ section', 'Targeted service CTA'],
  },
  {
    id: 'bp-srv-2',
    pageType: 'service',
    category: 'service',
    title: 'Web Development Service Page',
    targetUrl: '/services/web-development',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated page targeting technical web development services, CMS capabilities, and eCommerce solutions.',
    keyElements: ['Targeted H1 with development keywords', 'Technology stack breakdown (WordPress, React, etc.)', 'Security & speed optimization features', 'Related service cross-linking'],
  },
  {
    id: 'bp-srv-3',
    pageType: 'service',
    category: 'service',
    title: 'Graphic Design Service Page',
    targetUrl: '/services/graphic-design',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated page targeting branding, logo design, and marketing collateral.',
    keyElements: ['Targeted H1 with graphic design keywords', 'Branding packages & design process', 'Visual portfolio gallery', 'Client feedback'],
  },

  // 3. Location Hubs (Multi-Location Strategy)
  {
    id: 'bp-loc-1',
    pageType: 'location',
    category: 'location',
    title: 'Perth Location Hub',
    targetUrl: '/perth',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated location landing page for Perth expansion, following the Lecture 5 multi-service content scaling model (H1 for highest volume service).',
    keyElements: ['H1 for highest volume service (Web Design Perth, SV 500)', 'H2 for secondary services (Dev, Graphic Design)', 'Perth local context, suburbs served, and landmarks', 'Perth contact options & map'],
  },
  {
    id: 'bp-loc-2',
    pageType: 'location',
    category: 'location',
    title: 'Adelaide Location Hub',
    targetUrl: '/adelaide',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated location landing page for Adelaide market targeting local business clients.',
    keyElements: ['Localized H1 & H2 heading hierarchy', 'Adelaide specific service packages', 'Local Adelaide client testimonials'],
  },

  // 4. Trust & Social Proof Pages
  {
    id: 'bp-rev',
    pageType: 'reviews',
    category: 'trust_social',
    title: 'Customer Reviews & Testimonials',
    targetUrl: '/reviews',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Showcase authentic local customer feedback, video testimonials, Google review widgets, and star ratings to build overwhelming Google & user trust.',
    keyElements: ['Google Reviews embed badge & live feed', 'Video testimonials / client quotes', 'Aggregate star rating schema markup', 'Direct button to leave a new Google review'],
  },
  {
    id: 'bp-cases',
    pageType: 'case_studies',
    category: 'trust_social',
    title: 'Case Studies / Work Portfolio',
    targetUrl: '/case-studies',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Demonstrate real client problem-solving, before & after metrics, verifiable outcomes, and local credibility.',
    keyElements: ['Client industry & challenge breakdown', 'Strategy implemented & SEO / web results', 'Before vs. after visual comparison', 'Client quote / endorsement'],
  },
  {
    id: 'bp-feed',
    pageType: 'feedback',
    category: 'trust_social',
    title: 'Client Feedback & Review Us Page',
    targetUrl: '/feedback',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Dedicated page highlighted in Lecture 4 allowing customers to submit direct feedback, review their experience, and redirect to Google Business Profile.',
    keyElements: ['Interactive 5-star rating selector', 'Direct Google review profile quick link', 'Internal customer feedback form', 'Customer care contact'],
  },
  {
    id: 'bp-faq',
    pageType: 'faq',
    category: 'trust_social',
    title: 'Frequently Asked Questions (FAQs)',
    targetUrl: '/faq',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Answer common buyer questions, objections, turnaround times, and pricing models to improve conversions and capture Google rich snippet PAA results.',
    keyElements: ['Accordion-style categorized FAQ list', 'FAQPage JSON-LD schema markup', 'Clear answers with internal links', 'Inquiry CTA if question not answered'],
  },

  // 5. Authority & Education
  {
    id: 'bp-blog',
    pageType: 'blog',
    category: 'authority',
    title: 'Blog / Knowledge Base',
    targetUrl: '/blog',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Publish helpful, informative articles and guides for clients to build topical authority and target informational search queries.',
    keyElements: ['Topic categorization & search bar', 'Author bios with E-E-A-T credentials', 'Internal links to relevant service pages', 'Publication dates & reading time'],
  },

  // 6. Legal & Compliance Footer Pages (Crucial Trust Signals)
  {
    id: 'bp-priv',
    pageType: 'privacy',
    category: 'legal',
    title: 'Privacy Policy',
    targetUrl: '/privacy-policy',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Required by law and Google Quality Guidelines. Details how user data, cookies, analytics, and inquiry information are collected and protected.',
    keyElements: ['Data collection disclosure (forms, analytics)', 'Cookie policy & opt-out info', 'Third-party tools disclosure', 'Contact email for data privacy requests'],
  },
  {
    id: 'bp-terms',
    pageType: 'terms',
    category: 'legal',
    title: 'Terms of Service / Terms of Use',
    targetUrl: '/terms-of-use',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Defines rules, payment terms, intellectual property, and limitations of liability for using the website and services.',
    keyElements: ['Service agreements & payment terms', 'Intellectual property rights', 'Limitation of liability clauses', 'Governing local jurisdiction'],
  },
  {
    id: 'bp-disc',
    pageType: 'disclaimer',
    category: 'legal',
    title: 'Disclaimer',
    targetUrl: '/disclaimer',
    isPlanned: true,
    isBuilt: false,
    purpose: 'Protects the business regarding informational content accuracy, third-party links, and earnings or outcome guarantees.',
    keyElements: ['Professional advice disclaimer', 'No guaranteed outcome disclaimer', 'Third-party external link notice', 'Update and revision policy'],
  },
];

export const DEFAULT_TRUST_SIGNALS: TrustSignalItem[] = [
  {
    id: 'ts-ssl',
    name: 'SSL Certificate (HTTPS Encryption)',
    category: 'security',
    description: 'Active 256-bit SSL certificate ensuring secure encrypted communication and green padlock icon across 100% of website pages.',
    isImplemented: true,
    locationOnSite: 'Domain / All Pages',
    notes: 'Enforced via HTTPS 301 redirection in .htaccess or hosting panel',
  },
  {
    id: 'ts-nap-footer',
    name: 'Exact Matching NAP in Global Footer',
    category: 'nap',
    description: 'Business Name, Full Street Address, City, State, Zip, and Phone number displayed identically on every single page footer.',
    isImplemented: false,
    locationOnSite: 'Global Site Footer',
    notes: 'Zero-tolerance rule: exact same punctuation and format as Google Business Profile',
  },
  {
    id: 'ts-click-phone',
    name: 'Click-to-Call Phone in Header & Mobile Sticky Bar',
    category: 'conversion',
    description: 'Direct telephone link (tel: protocol) in website header and mobile sticky bar enabling instant one-tap calling for local clients.',
    isImplemented: false,
    locationOnSite: 'Header & Mobile Navigation',
    notes: 'Formatted with local area code to reinforce geographic proximity',
  },
  {
    id: 'ts-gmaps',
    name: 'Interactive Google Maps Embed',
    category: 'nap',
    description: 'Direct Google Maps iframe embed showing the official verified business location, pin, and driving directions.',
    isImplemented: false,
    locationOnSite: 'Contact Us & Location Pages',
    notes: 'Embedded directly from the verified Google Business Profile CID / Share link',
  },
  {
    id: 'ts-reviews-badge',
    name: 'Verified Customer Reviews & Rating Badge',
    category: 'social_proof',
    description: 'Live badge showing verified Google customer reviews, overall star rating (e.g. 4.9/5), and total review count to build trust.',
    isImplemented: false,
    locationOnSite: 'Homepage, About, Reviews Page',
    notes: 'Reinforces user trust signals emphasized in Lecture 5',
  },
  {
    id: 'ts-licenses',
    name: 'Professional Licenses, Insurance & Accreditation Badges',
    category: 'accreditation',
    description: 'Badges and license numbers (e.g. State Licensed, Insured, Bonded, BBB Accredited, Industry Association badges) displayed prominently.',
    isImplemented: false,
    locationOnSite: 'Footer, About Us, Homepage Hero',
    notes: 'Establishes Google E-E-A-T and removes visitor hesitation',
  },
  {
    id: 'ts-schema',
    name: 'LocalBusiness Schema.org JSON-LD Structured Data',
    category: 'nap',
    description: 'Comprehensive schema markup specifying @type, name, address, geo-coordinates, telephone, openingHours, priceRange, and sameAs links.',
    isImplemented: false,
    locationOnSite: 'Site <head> / Global Header',
    notes: 'Validates entity relationships directly to Google search crawlers',
  },
  {
    id: 'ts-clear-cta',
    name: 'Clear, Frictionless Call-To-Action (CTA)',
    category: 'conversion',
    description: 'High-contrast conversion buttons ("Get a Free Quote", "Book an Appointment", "Call Now") present in hero and after service sections.',
    isImplemented: false,
    locationOnSite: 'Hero Section & Service Blocks',
    notes: 'Guides local searchers seamlessly into leads',
  },
];

export const DEFAULT_ONPAGE_CHECKLIST: Record<string, OnPageChecklistReviewItem> = {
  'chk-cat-1': {
    id: 'chk-cat-1',
    category: 'categories_menu',
    title: 'Service Categories Created in CMS',
    description: 'Service categories created under WordPress Posts -> Categories (e.g. Services, Web Design, Digital Marketing).',
    isReviewed: false,
  },
  'chk-cat-2': {
    id: 'chk-cat-2',
    category: 'categories_menu',
    title: 'Categories Added to Primary Menu',
    description: 'Categories added to primary navigation menu via Appearance -> Menus -> View All -> Add to Menu.',
    isReviewed: false,
  },
  'chk-cat-3': {
    id: 'chk-cat-3',
    category: 'categories_menu',
    title: 'Subpages Assigned Under Relevant Categories',
    description: 'New service and location subpages assigned to their respective parent category in WordPress.',
    isReviewed: false,
  },
  'chk-cat-4': {
    id: 'chk-cat-4',
    category: 'categories_menu',
    title: '1-Click Away Rule Verified',
    description: 'All key service pages and category hubs are maximum 1 click away from primary site navigation.',
    isReviewed: false,
  },
  'chk-loc-1': {
    id: 'chk-loc-1',
    category: 'location_strategy',
    title: 'Single vs. Multi-Location Strategy Defined',
    description: 'Business model assessed: Single location (Sydney) or multi-location expansion (Sydney + Adelaide + Perth + Melbourne).',
    isReviewed: false,
  },
  'chk-loc-2': {
    id: 'chk-loc-2',
    category: 'location_strategy',
    title: 'Expansion Cities Mapped',
    description: 'All target expansion markets identified with dedicated local pages or multi-service hub structures.',
    isReviewed: false,
  },
  'chk-str-1': {
    id: 'chk-str-1',
    category: 'site_structure_pages',
    title: 'Core Pages Architecture Planned & Built',
    description: 'Core foundational pages created: Homepage (main landing page + primary NAP), About Us (team credentials & E-E-A-T), and Contact Us (interactive map & verified contact options).',
    isReviewed: false,
  },
  'chk-str-2': {
    id: 'chk-str-2',
    category: 'site_structure_pages',
    title: 'Dedicated Individual Pages for Every Service',
    description: 'Every offered service has its own dedicated subpage with custom URL and keyword focus (e.g. Web Design, Web Dev, Graphic Design) rather than lumped into one single page.',
    isReviewed: false,
  },
  'chk-str-3': {
    id: 'chk-str-3',
    category: 'site_structure_pages',
    title: 'Trust & Social Proof Pages Implemented',
    description: 'Reviews/Testimonials page, Case Studies / Work Portfolio, Feedback page, and FAQs page created to satisfy visitor intent and Google Quality guidelines.',
    isReviewed: false,
  },
  'chk-str-4': {
    id: 'chk-str-4',
    category: 'site_structure_pages',
    title: 'Legal & Compliance Footer Pages Built',
    description: 'Mandatory legal trust pages created and linked in the global footer: Privacy Policy, Terms of Service, and Disclaimer.',
    isReviewed: false,
  },
  'chk-trs-1': {
    id: 'chk-trs-1',
    category: 'trust_signals',
    title: 'On-Site Trust Signals & Security Verified',
    description: 'SSL certificate (HTTPS), global footer NAP, header click-to-call phone number, Google Maps iframe embed, verified review badges, and LocalBusiness schema markup implemented.',
    isReviewed: false,
  },
  'chk-cnt-1': {
    id: 'chk-cnt-1',
    category: 'content_hierarchy',
    title: 'H1 Assigned to Highest Search Volume Service',
    description: 'The highest search volume and highest competition service (e.g. Web Design Perth, SV 500) is assigned as the single H1 heading.',
    isReviewed: false,
  },
  'chk-cnt-2': {
    id: 'chk-cnt-2',
    category: 'content_hierarchy',
    title: 'Secondary Services Formatted as H2 Headings',
    description: 'Secondary and tertiary services (e.g. Web Development Perth, Graphic Design Perth) are formatted as H2 headings, never duplicate H1s.',
    isReviewed: false,
  },
  'chk-cnt-3': {
    id: 'chk-cnt-3',
    category: 'content_hierarchy',
    title: 'Word Counts Scaled by Search Volume',
    description: 'Content lengths scaled proportionally: 300–500 words for H1 primary service, 200–300 words for H2 secondary services.',
    isReviewed: false,
  },
  'chk-cnt-4': {
    id: 'chk-cnt-4',
    category: 'content_hierarchy',
    title: 'Natural Keyword Density & Placement',
    description: 'Primary keyword used in H1 and twice in body; secondary keywords in H2 and 1–2 times in body without keyword stuffing.',
    isReviewed: false,
  },
  'chk-prm-1': {
    id: 'chk-prm-1',
    category: 'permalinks_redirects',
    title: 'Clean Permalink Structure Audited',
    description: 'URLs audited for clean, readable permalink structure (e.g. website.com/perth or website.com/web-design-perth).',
    isReviewed: false,
  },
  'chk-prm-2': {
    id: 'chk-prm-2',
    category: 'permalinks_redirects',
    title: 'Existing High-Ranking URLs Preserved',
    description: 'If existing client location pages are already ranking well, permalinks are preserved untouched to protect link equity.',
    isReviewed: false,
  },
  'chk-prm-3': {
    id: 'chk-prm-3',
    category: 'permalinks_redirects',
    title: '301 Permanent Redirects Configured for Changed URLs',
    description: 'If messy/long permalinks must be changed, 301 redirects are mapped and verified via Yoast Advanced Settings or 301 plugin.',
    isReviewed: false,
  },
  'chk-nap-1': {
    id: 'chk-nap-1',
    category: 'nap_gmb',
    title: 'Business Name 100% Exact Match (Zero Comma Difference)',
    description: 'Business name verified to be 100% identical everywhere across site, GMB, and citation directories with no variation.',
    isReviewed: false,
  },
  'chk-nap-2': {
    id: 'chk-nap-2',
    category: 'nap_gmb',
    title: 'Full Address & Exact Postal/Zip Code Verified',
    description: 'Street address, city, state, and exact postal/zip code (e.g. 66000) verified to establish physical Google trust.',
    isReviewed: false,
  },
  'chk-nap-3': {
    id: 'chk-nap-3',
    category: 'nap_gmb',
    title: 'Local Regional Area Code Phone Number Verified',
    description: 'Local telephone number with regional area code verified in site footer, header, schema, and contact pages.',
    isReviewed: false,
  },
  'chk-nap-4': {
    id: 'chk-nap-4',
    category: 'nap_gmb',
    title: 'Google Business Profile (GMB) Consistency Audit',
    description: 'GMB profile matches website footer NAP, hours, and primary service categories perfectly to prevent suspensions.',
    isReviewed: false,
  },
  'chk-sig-1': {
    id: 'chk-sig-1',
    category: 'final_signoff',
    title: 'On-Page Walkthrough Sign-Off for Pipeline',
    description: 'Complete On-Page SEO walkthrough reviewed, verified, and signed off for pipeline handoff.',
    isReviewed: false,
  },
};

const defaultNapProfile: NapProfile = {
  exactBusinessName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  phoneNumber: '',
  regionalAreaCode: '',
  gmbVerified: false,
  websiteFooterVerified: false,
  socialProfilesMatch: false,
  localDirectoriesMatch: false,
  notes: '',
};

const defaultPerthLocationPlan: MultiServiceLocationPlan = {
  targetCity: 'Perth',
  isPrimaryLocation: false,
  locationPageUrl: '/perth',
  h1Service: {
    id: 'srv-1',
    serviceName: 'Web Design',
    headingTag: 'H1',
    searchVolume: 500,
    targetKeyword: 'Web Design Perth',
    recommendedWordCount: 350,
    actualWordCount: 350,
    keywordOccurrencesInContent: 2,
    notes: 'Highest search volume & competition. Assigned single H1.',
  },
  secondaryServices: [
    {
      id: 'srv-2',
      serviceName: 'Web Development',
      headingTag: 'H2',
      searchVolume: 300,
      targetKeyword: 'Web Development Perth',
      recommendedWordCount: 280,
      actualWordCount: 280,
      keywordOccurrencesInContent: 2,
      notes: 'Medium volume. Assigned H2 tag.',
    },
    {
      id: 'srv-3',
      serviceName: 'Graphic Design',
      headingTag: 'H2',
      searchVolume: 200,
      targetKeyword: 'Graphic Design Perth',
      recommendedWordCount: 220,
      actualWordCount: 220,
      keywordOccurrencesInContent: 1,
      notes: 'Lowest search volume. Assigned H2 tag.',
    },
  ],
};

export class OnPageStorageService {
  static getAllProjects(): OnPageSeoProject[] {
    try {
      const raw = localStorage.getItem(ONPAGE_PROJECTS_KEY);
      let list: OnPageSeoProject[] = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];

      // Purge any accidental Apex project
      list = list.filter(p => !p.clientName?.toLowerCase().includes('apex') && !p.id.toLowerCase().includes('apex'));

      // Migrate any legacy "Reflect Window & Door" to "Reflect Window Cleaning"
      list = list.map(p => {
        if (p.clientName?.includes('Door') || p.websiteUrl?.includes('reflectwindow.com')) {
          return {
            ...p,
            clientName: 'Reflect Window Cleaning',
            websiteUrl: 'https://reflectwindowcleaning.com',
            napProfile: {
              ...p.napProfile,
              exactBusinessName: 'Reflect Window Cleaning',
            },
            executiveSummary: p.executiveSummary?.replace(/Reflect Window & Door/g, 'Reflect Window Cleaning') || p.executiveSummary,
          };
        }
        return p;
      });

      const hasTrucking = list.some(p => p.clientName?.toLowerCase().includes('trucking') || p.websiteUrl?.toLowerCase().includes('trucking'));
      if (!hasTrucking) {
        list.push(this.createProject('United Trucking Supplies', 'https://unitedtruckingsupplies.com', 'Trinidad and Tobago', 'audit_united_trucking', 'kw-project-united-trucking'));
      }

      const hasReflect = list.some(p => p.clientName?.toLowerCase().includes('reflect') || p.websiteUrl?.toLowerCase().includes('reflect'));
      if (!hasReflect) {
        list.push(this.createProject('Reflect Window Cleaning', 'https://reflectwindowcleaning.com', 'Edmonton, AB', 'audit_reflect_window', 'kw-project-reflect-window'));
      }

      localStorage.setItem(ONPAGE_PROJECTS_KEY, JSON.stringify(list));

      const activeId = this.getActiveProjectId();
      if (!activeId || activeId.includes('apex')) {
        this.setActiveProjectId(list[0].id);
      }

      return list;
    } catch {
      const p1 = this.createProject('United Trucking Supplies', 'https://unitedtruckingsupplies.com', 'Trinidad and Tobago', 'audit_united_trucking', 'kw-project-united-trucking');
      const p2 = this.createProject('Reflect Window Cleaning', 'https://reflectwindowcleaning.com', 'Edmonton, AB', 'audit_reflect_window', 'kw-project-reflect-window');
      const list = [p1, p2];
      localStorage.setItem(ONPAGE_PROJECTS_KEY, JSON.stringify(list));
      this.setActiveProjectId(p1.id);
      return list;
    }
  }

  static getProjectById(id: string): OnPageSeoProject | null {
    const list = this.getAllProjects();
    return list.find(p => p.id === id) || null;
  }

  static getActiveProjectId(): string | null {
    return localStorage.getItem(ACTIVE_ONPAGE_PROJECT_KEY);
  }

  static setActiveProjectId(id: string): void {
    localStorage.setItem(ACTIVE_ONPAGE_PROJECT_KEY, id);
  }

  static createProject(
    clientName: string,
    websiteUrl: string,
    primaryLocation: string = 'Sydney',
    linkedAuditId?: string,
    linkedKeywordProjectId?: string
  ): OnPageSeoProject {
    const raw = localStorage.getItem(ONPAGE_PROJECTS_KEY);
    let projects: OnPageSeoProject[] = [];
    try {
      projects = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(projects)) projects = [];
    } catch {
      projects = [];
    }
    const newProject: OnPageSeoProject = {
      id: `onpage-project-${Date.now()}`,
      clientName,
      websiteUrl,
      linkedAuditId,
      linkedKeywordProjectId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'in_progress',
      locationModel: 'multi_location',
      primaryLocation,
      expansionCities: ['Perth', 'Adelaide', 'Melbourne'],
      categories: [
        { id: 'cat-1', name: 'Services', slug: 'services', addedToMenu: true, isOneClickAway: true, notes: 'Main service category dropdown in primary navigation' },
        { id: 'cat-2', name: 'Locations', slug: 'locations', addedToMenu: true, isOneClickAway: true, notes: 'Target multi-location expansion category' },
      ],
      subpages: [
        { id: 'sub-1', title: 'Web Design Sydney', slug: 'web-design-sydney', categoryId: 'cat-1', targetKeyword: 'Web Design Company Sydney', addedToMenu: true, isOneClickAway: true },
        { id: 'sub-2', title: 'Web Development Sydney', slug: 'web-development-sydney', categoryId: 'cat-1', targetKeyword: 'Web Development Sydney', addedToMenu: true, isOneClickAway: true },
        { id: 'sub-3', title: 'Graphic Design Sydney', slug: 'graphic-design-sydney', categoryId: 'cat-1', targetKeyword: 'Graphic Design Services in Sydney', addedToMenu: true, isOneClickAway: true },
        { id: 'sub-4', title: 'Perth Location Hub', slug: 'perth', categoryId: 'cat-2', targetKeyword: 'Web Design Perth', addedToMenu: true, isOneClickAway: true },
      ],
      sitePagesArchitecture: {
        totalPagesPlanned: DEFAULT_PAGE_BLUEPRINTS.length,
        blueprints: [...DEFAULT_PAGE_BLUEPRINTS],
        trustSignals: [...DEFAULT_TRUST_SIGNALS],
      },
      locationPlans: [defaultPerthLocationPlan],
      permalinkAudits: [
        {
          id: 'prm-1',
          pageTitle: 'Perth Location Page',
          oldUrl: `${websiteUrl.replace(/\/$/, '')}/location-pages-2024-perth-special-services/`,
          newUrl: `${websiteUrl.replace(/\/$/, '')}/perth`,
          isLongOrMessy: true,
          decision: 'update_and_redirect',
          redirectType: '301',
          redirectTool: 'yoast',
          status: 'pending',
          notes: 'Old URL is excessively long and messy. Set 301 redirect to clean /perth URL in Yoast.',
        },
      ],
      napProfile: {
        ...defaultNapProfile,
        exactBusinessName: clientName,
        city: primaryLocation,
      },
      checklist: { ...DEFAULT_ONPAGE_CHECKLIST },
      executiveSummary: `On-Page SEO architecture plan for ${clientName}. Configured WordPress 1-click away service hierarchy, multi-service location page content scaling (H1 for highest search volume), 301 redirect mapping, and strict NAP consistency verification across GMB and local citations.`,
    };

    projects.unshift(newProject);
    localStorage.setItem(ONPAGE_PROJECTS_KEY, JSON.stringify(projects));
    this.setActiveProjectId(newProject.id);
    return newProject;
  }

  static saveProject(project: OnPageSeoProject): void {
    const projects = this.getAllProjects();
    const idx = projects.findIndex(p => p.id === project.id);
    const updated = {
      ...project,
      updatedAt: new Date().toISOString(),
    };
    if (idx >= 0) {
      projects[idx] = updated;
    } else {
      projects.unshift(updated);
    }
    localStorage.setItem(ONPAGE_PROJECTS_KEY, JSON.stringify(projects));
    cloudSyncService.saveOnPageProjectToCloud(updated).catch(() => {});
  }

  static deleteProject(id: string): void {
    const projects = this.getAllProjects().filter(p => p.id !== id);
    localStorage.setItem(ONPAGE_PROJECTS_KEY, JSON.stringify(projects));
    if (this.getActiveProjectId() === id) {
      localStorage.removeItem(ACTIVE_ONPAGE_PROJECT_KEY);
    }
  }
}
